import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { setAxiosEnvironment } from "@/api/client";
import AuthService from "@/api/services/auth";
import { useAuth } from "@/providers/AuthProvider";
import { Environment } from "@/providers/AuthProvider";

export type PasswordlessCodePayload = {
  email: string;
  code: string;
};

export function useAuthStatus() {
  const { setEnvironment } = useAuth();

  return useQuery({
    queryKey: ["auth", "status"],
    queryFn: async () => {
      const data = await AuthService.getAuthStatus();
      setEnvironment(data.environment);
      setAxiosEnvironment(data.environment);
      return data;
    },
    retry: false,
  });
}

export function useCheckSSOEnabled() {
  return useMutation({
    mutationFn: (email: string) => AuthService.postSSOEnabled(email),
  });
}

export function useSSOLogin() {
  return useMutation({
    mutationFn: (email: string) => AuthService.postSSOLogin(email),
  });
}

export function useLoginToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) => AuthService.postLoginToken({ code, state }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "status"] });
    },
  });
}

export function usePasswordlessLogin() {
  return useMutation({
    mutationFn: (email: string) => AuthService.postPasswordlessLogin(email),
  });
}

export function usePasswordlessCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PasswordlessCodePayload) => AuthService.postPasswordlessCode(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "status"] });
    },
  });
}

export function useGoogleOauthLogin() {
  return useMutation({
    mutationFn: () => AuthService.postGoogleOauthLogin(),
  });
}

export function useLogout() {
  const { setEnvironment } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => AuthService.getLogOut(),
    onSuccess: () => {
      setEnvironment(Environment.PROD);
      setAxiosEnvironment(Environment.PROD);
      // Clear all cached data
      queryClient.clear();
      // Go to login
      navigate("/login");
    },
  });
}

export function useUpdateEnvironment() {
  const { setEnvironment } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ environment }: { environment: Environment }) => AuthService.updateEnvironment({ environment }),
    onSuccess: (data) => {
      setEnvironment(data.environment);
      setAxiosEnvironment(data.environment);
      // Clear all cached data and redirect to overview
      queryClient.clear();
      navigate("/overview");
    },
  });
}
